import { db, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  addDoc,
  doc,
  getDoc,
} from "firebase/firestore";

const LockBoxService = {
  fetchDocumentsFromLockbox: async (userId, collectionName) => {
    try {
      const documentsCollectionRef = collection(
        db,
        "lockbox",
        userId,
        collectionName
      );
      const documentsSnapshot = await getDocs(documentsCollectionRef);

      const documentsList = documentsSnapshot.docs.map((doc) => {
        const docData = doc.data();
        return {
          id: doc.id,
          ...docData,
        };
      });

      return documentsList;
    } catch (error) {
      console.error("Error fetching documents:", error);
      throw error;
    }
  },

  addDocumentToLockbox: async (userId, documentData, type) => {
    try {
      const documentsCollectionRef = collection(db, "lockbox", userId, type);

      await addDoc(documentsCollectionRef, documentData);

      console.log("Document successfully added to the lockbox!");
    } catch (error) {
      console.error("Error adding document to lockbox: ", error);
      throw error;
    }
  },

  updateDocumentInLockbox: async (userId, documentId, documentData, type) => {
    try {
      if (!documentId) {
        throw new Error("documentId is required for updating.");
      }

      const documentsCollectionRef = collection(db, "lockbox", userId, type);

      // Use the appropriate ID field based on the collection type
      const idField = type === "photos_data" ? "PhotosID" : "documentsID";

      const querySnapshot = await getDocs(
        query(documentsCollectionRef, where(idField, "==", documentId))
      );

      if (querySnapshot.empty) {
        console.error(`No document found with ID: ${documentId} in ${type}`);
        return;
      }

      const docRef = querySnapshot.docs[0].ref; // Get the actual document reference

      await updateDoc(docRef, documentData);

      console.log(`Document ${documentId} successfully updated in ${type}!`);
    } catch (error) {
      console.error("Error updating document in lockbox:", error);
      throw error;
    }
  },

  softDeleteItemInLockbox: async (userId, documentId, type) => {
    try {
      if (!documentId) {
        throw new Error("documentId is required");
      }

      const documentsCollectionRef = collection(db, "lockbox", userId, type);
      const documentRef = doc(documentsCollectionRef, documentId);

      // Update the document to mark it as deleted
      const updatedData = {
        is_deleted: true,
        modified_date: new Date().toLocaleDateString("en-US"),
      };

      await updateDoc(documentRef, updatedData);
      console.log("Document successfully marked as deleted in lockbox!");

      // If this is a partner document in the center's lockbox, update all partner copies
      if (type === "partner_documents") {
        // Get the center's document to get centerId
        const centerUserRef = doc(db, "users", userId);
        const centerUserDoc = await getDoc(centerUserRef);
        let centerId = null;
        if (centerUserDoc.exists()) {
          const centerData = centerUserDoc.data();
          centerId = centerData.center_id;
        }
        try {
          // Find all users whose center_id array contains userId
          const usersRef = collection(db, "users");
          const usersQuery = query(
            usersRef,
            where("center_id", "array-contains", centerId)
          );
          const usersSnapshot = await getDocs(usersQuery);

          if (!usersSnapshot.empty) {
            for (const userDoc of usersSnapshot.docs) {
              const partnerUserId = userDoc.id;

              // Find the partner's copy of this document
              const partnerDocsRef = collection(
                db,
                "lockbox",
                partnerUserId,
                "center_documents"
              );

              const partnerDocQuery = query(
                partnerDocsRef,
                where("id", "==", documentId)
              );

              const partnerDocSnapshot = await getDocs(partnerDocQuery);

              // Update all matching documents for this partner
              for (const doc of partnerDocSnapshot.docs) {
                const partnerDocRef = doc.ref;
                await updateDoc(partnerDocRef, {
                  is_deleted: true,
                  modified_date: new Date().toLocaleDateString("en-US"),
                });
                console.log(`Updated partner copy for user ${partnerUserId}`);
              }
            }
          }
        } catch (error) {
          console.error("Error updating partner copies:", error);
          // Continue with the main deletion even if partner updates fail
        }
      }
    } catch (error) {
      console.error("Error marking document as deleted in lockbox: ", error);
      throw error;
    }
  },

  // Delete a document from the "documents_data" collection based on documentId
  deleteDocumentFromLockbox: async (userId, documentId, type) => {
    try {
      if (!documentId) {
        throw new Error("documentId is undefined or null");
      }

      // Create a query to find the document with the given documentId
      const documentsCollectionRef = collection(db, "lockbox", userId, type);
      const idField = type === "documents_data" ? "documentsID" : "PhotosID";

      const q = query(documentsCollectionRef, where(idField, "==", documentId));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // Delete each matching document (should be only one in your case)
        querySnapshot.forEach(async (doc) => {
          const documentRef = doc.ref;
          await deleteDoc(documentRef);
        });
      } else {
        console.error("No document found with the given ID to delete.");
        throw new Error("No document found with the given ID to delete.");
      }
    } catch (error) {
      console.error("Error deleting document from lockbox: ", error);
      throw error;
    }
  },

  submitAccessRequest: async ({
    userId,
    documents,
    message,
    requested_by,
    requested_by_org,
    requested_by_role,
    requested_by_name,
  }) => {
    try {
      const requestData = {
        documents,
        message,
        status: "pending",
        requested_by,
        requested_by_name,
        requested_by_org,
        requested_by_role,
        requested_at: new Date().toISOString(),
        is_deleted: false,
      };

      const accessRequestsRef = collection(
        db,
        "lockbox",
        userId,
        "access_requests"
      );
      await addDoc(accessRequestsRef, requestData);
      console.log("Access request successfully added to the lockbox!");
    } catch (error) {
      console.error("Error submitting access request:", error);
      throw error;
    }
  },

  copyNewPartnerDocument: async (userId, newDocument) => {
    try {
      if (!userId || !newDocument) {
        console.error("Missing userId or document data.");
        return;
      }

      // Check if the document is Active before copying
      if (newDocument.status !== "Active") {
        console.error(
          `Document ${newDocument.documentsID} is not Active. Skipping copy.`
        );
        return;
      }

      // Step 1: Find all users whose center_id array contains userId
      const usersRef = collection(db, "users");
      const usersQuery = query(
        usersRef,
        where("center_id", "array-contains", userId)
      );
      const usersSnapshot = await getDocs(usersQuery);

      if (usersSnapshot.empty) {
        console.error("No users found with matching center_id.");
        return;
      }

      const matchedUsers = usersSnapshot.docs.map((doc) => doc.id);

      // Step 2: Copy the new document to each user's center_documents
      for (const matchedUserId of matchedUsers) {
        const userLockboxRef = collection(
          db,
          "lockbox",
          matchedUserId,
          "center_documents"
        );

        const newDocumentData = {
          ...newDocument,
          // Document identification
          documentsID: generateUniqueId(),
          original_documentsID: newDocument.documentsID,
          copied_from_center: userId,

          // Timestamps
          copied_at: new Date().toISOString(),
          modified_date: new Date().toLocaleDateString("en-US"),
          date_inserted: new Date().toLocaleDateString("en-US"),

          // Signature requirements - copy from original
          partner_signature_required:
            newDocument.partner_signature_required || false,
          center_signature_required:
            newDocument.center_signature_required || false,

          // Initialize signature fields as null
          partner_signature: null,
          center_signature: null,
          partner_acknowledged: null,
          center_acknowledged: null,

          // Status fields
          status: newDocument.status || "Active",
          is_deleted: false,

          // Document content
          type: newDocument.type,
          description: newDocument.description,
          photodocuments: newDocument.photodocuments,

          // Access control
          access: newDocument.access || [],
        };

        await addDoc(userLockboxRef, newDocumentData);
        console.log(`Created new document copy for user ${matchedUserId}`);
      }
    } catch (error) {
      console.error("Error copying new partner document:", error);
      throw error;
    }
  },

  updateCopyPartnerDocuments: async (userId, documentId, updatedDocument) => {
    try {
      if (!userId || !documentId) {
        console.error("Missing required fields:", { userId, documentId });
        return;
      }

      console.log("Starting update with:", {
        userId,
        documentId,
        updatedDocument,
      });

      // Step 1: Find all users whose center_id array contains userId
      const usersRef = collection(db, "users");
      const usersQuery = query(
        usersRef,
        where("center_id", "array-contains", userId)
      );
      const usersSnapshot = await getDocs(usersQuery);

      if (usersSnapshot.empty) {
        console.log("No partner users found for center:", userId);
        return;
      }

      const matchedUsers = usersSnapshot.docs.map((doc) => doc.id);
      console.log("Found partner users:", matchedUsers);

      // Step 2: Loop through each matched user
      for (const matchedUserId of matchedUsers) {
        try {
          const userLockboxRef = collection(
            db,
            "lockbox",
            matchedUserId,
            "center_documents"
          );

          // Step 3: Find document with the same original_documentsID
          const existingDocQuery = query(
            userLockboxRef,
            where("original_documentsID", "==", documentId)
          );
          const existingDocSnapshot = await getDocs(existingDocQuery);

          if (existingDocSnapshot.empty) {
            console.log(`No matching document found for user ${matchedUserId}`);
            continue;
          }

          // Update all matching documents
          for (const existingDoc of existingDocSnapshot.docs) {
            try {
              const existingDocRef = doc(
                db,
                "lockbox",
                matchedUserId,
                "center_documents",
                existingDoc.id
              );

              const existingData = existingDoc.data();

              // Sanitize the updated document data
              const sanitizedUpdatedDoc = Object.entries(
                updatedDocument || {}
              ).reduce((acc, [key, value]) => {
                if (value !== undefined && value !== null) {
                  acc[key] = value;
                }
                return acc;
              }, {});

              // Determine if this is a signature document or acknowledgment-only
              const isSignatureRequired =
                sanitizedUpdatedDoc.partner_signature_required ||
                sanitizedUpdatedDoc.center_signature_required;

              // Create the update data based on document type
              const updatedData = {
                ...sanitizedUpdatedDoc,
                // Document identification
                original_documentsID:
                  existingData.original_documentsID || documentId,
                copied_from_center: existingData.copied_from_center || userId,
                documentsID: existingData.documentsID,

                // Timestamps
                copied_at: new Date().toISOString(),
                modified_date: new Date().toLocaleDateString("en-US"),
                date_inserted: existingData.date_inserted,

                // If signature is required, handle signature fields
                ...(isSignatureRequired
                  ? {
                      partner_signature_required:
                        sanitizedUpdatedDoc.partner_signature_required ?? false,
                      center_signature_required:
                        sanitizedUpdatedDoc.center_signature_required ?? false,
                      partner_signature: existingData.partner_signature,
                      center_signature: existingData.center_signature,
                      // Preserve detailed acknowledgment information
                      partner_acknowledged: existingData.partner_acknowledged,
                      partner_acknowledged_by:
                        existingData.partner_acknowledged_by,
                      partner_acknowledged_date:
                        existingData.partner_acknowledged_date,
                      partner_acknowledged_name:
                        existingData.partner_acknowledged_name,
                      center_acknowledged: existingData.center_acknowledged,
                      center_acknowledged_by:
                        existingData.center_acknowledged_by,
                      center_acknowledged_date:
                        existingData.center_acknowledged_date,
                      center_acknowledged_name:
                        existingData.center_acknowledged_name,
                    }
                  : {
                      // If acknowledgment only
                      partner_signature_required: false,
                      center_signature_required: false,
                      partner_signature: null,
                      center_signature: null,
                      // Preserve detailed acknowledgment information
                      partner_acknowledged: existingData.partner_acknowledged,
                      partner_acknowledged_by:
                        existingData.partner_acknowledged_by,
                      partner_acknowledged_date:
                        existingData.partner_acknowledged_date,
                      partner_acknowledged_name:
                        existingData.partner_acknowledged_name,
                      center_acknowledged: existingData.center_acknowledged,
                      center_acknowledged_by:
                        existingData.center_acknowledged_by,
                      center_acknowledged_date:
                        existingData.center_acknowledged_date,
                      center_acknowledged_name:
                        existingData.center_acknowledged_name,
                    }),

                // Document content
                type: sanitizedUpdatedDoc.type || existingData.type,
                description:
                  sanitizedUpdatedDoc.description || existingData.description,
                status:
                  sanitizedUpdatedDoc.status || existingData.status || "Active",
                is_deleted: sanitizedUpdatedDoc.is_deleted ?? false,

                // Arrays
                access: Array.isArray(sanitizedUpdatedDoc.access)
                  ? sanitizedUpdatedDoc.access
                  : [],
                photodocuments: Array.isArray(
                  sanitizedUpdatedDoc.photodocuments
                )
                  ? sanitizedUpdatedDoc.photodocuments
                  : [],
              };

              // Remove any remaining undefined or null values
              const finalUpdateData = Object.entries(updatedData).reduce(
                (acc, [key, value]) => {
                  if (value !== undefined && value !== null) {
                    acc[key] = value;
                  }
                  return acc;
                },
                {}
              );

              console.log("Updating document with data:", finalUpdateData);
              await updateDoc(existingDocRef, finalUpdateData);
              console.log(
                `Successfully updated document ${existingDoc.id} for user ${matchedUserId}`
              );
            } catch (docUpdateError) {
              console.error(
                `Error updating individual document for user ${matchedUserId}:`,
                docUpdateError
              );
            }
          }
        } catch (userError) {
          console.error(`Error processing user ${matchedUserId}:`, userError);
        }
      }
    } catch (error) {
      console.error("Error in updateCopyPartnerDocuments:", error);
      throw error;
    }
  },

  copyDocumentsToNewPartner: async (userId, centerId) => {
    try {
      if (!userId || !centerId) {
        console.error("Missing userId or centerId.");
        return;
      }

      console.log("userId", userId);
      console.log("centerId", centerId);

      const sourceRef = collection(
        db,
        "lockbox",
        centerId,
        "partner_documents"
      );
      const sourceSnapshot = await getDocs(sourceRef);

      if (sourceSnapshot.empty) {
        console.log(
          `No documents found in lockbox/${centerId}/partner_documents.`
        );
        return;
      }

      for (const docSnap of sourceSnapshot.docs) {
        const docData = docSnap.data();

        if (docData.status !== "Active") {
          console.log(`Skipping inactive document ${docSnap.id}`);
          continue;
        }

        const documentIdToMatch = docData.documentsID || docSnap.id;
        const destinationRef = collection(
          db,
          "lockbox",
          userId,
          "center_documents"
        );

        // Check if document already exists by original_documentsID
        const existingQuery = query(
          destinationRef,
          where("original_documentsID", "==", documentIdToMatch)
        );
        const existingSnapshot = await getDocs(existingQuery);

        const newData = {
          ...docData,
          copied_from_center: centerId,
          copied_at: new Date().toISOString(),
          is_deleted: false,
        };

        if (!existingSnapshot.empty) {
          // If it exists, update the first found match
          const existingDoc = existingSnapshot.docs[0];
          const existingDocRef = doc(
            db,
            "lockbox",
            userId,
            "center_documents",
            existingDoc.id
          );

          await updateDoc(existingDocRef, newData);
          console.log(
            `Updated existing document ${existingDoc.id} for user ${userId}`
          );
        } else {
          // If not found, create new with new document ID
          const newDoc = {
            ...newData,
            documentsID: generateUniqueId(),
            original_documentsID: documentIdToMatch,
            copied_from_center: centerId,
            copied_at: new Date().toISOString(),
            is_deleted: false,
          };

          await addDoc(destinationRef, newDoc);
          console.log(
            `🆕 Created new copy of document ${docSnap.id} for user ${userId}`
          );
        }
      }

      console.log(
        `Finished copying documents from partner ${centerId} to user ${userId}`
      );
    } catch (error) {
      console.error("Error copying documents to new partner:", error);
    }
  },
};

function generateUniqueId(length = 28) {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}

export default LockBoxService;
